import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth, useAuthDispatch } from './AuthContext';
import { SET_AUTH_USER, USER_LOGOUT } from './actionTypes';
import type { AuthState } from '@/types';

const ApiContext = createContext<AxiosInstance | null>(null);

interface ApiProviderProps {
  children: ReactNode;
}

function buildApi(auth: AuthState, auth_dispatch: React.Dispatch<any>): AxiosInstance {
  const api = axios.create({
    baseURL: `${import.meta.env.VITE_API_URL}/v1`,
  });

  // Request interceptor to check token expiration before making requests
  api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    // Check if token is expired and refresh if needed
    const token = localStorage.getItem('accqsure_jwt');
    if (token && auth.user_manager && auth.user) {
      try {
        const user = await auth.user_manager.getUser();
        if (user && user.expires_at && user.expires_at < Date.now() / 1000) {
          // Token is expired, try to refresh it
          console.log('Token expired, attempting refresh...');
          try {
            const refreshedUser = await auth.user_manager.signinSilent();
            if (refreshedUser && refreshedUser.access_token) {
              // Update the token in localStorage and auth context
              localStorage.setItem(
                'accqsure_jwt',
                refreshedUser.access_token,
              );
              auth_dispatch({ type: SET_AUTH_USER, payload: refreshedUser });
              // Update the current request with the new token
              config.headers['Authorization'] =
                `Bearer ${refreshedUser.access_token}`;
              console.log('Token refreshed successfully');
            } else {
              // Silent refresh failed, redirect to login
              console.log('Silent refresh failed, redirecting to login');
              auth_dispatch({ type: USER_LOGOUT });
              auth.user_manager.signinRedirect();
              return Promise.reject(new Error('Token refresh failed'));
            }
          } catch (silentRefreshError) {
            console.log('Silent refresh failed:', silentRefreshError);
            // Silent refresh failed, redirect to login
            auth_dispatch({ type: USER_LOGOUT });
            auth.user_manager.signinRedirect();
            return Promise.reject(new Error('Token refresh failed'));
          }
        } else if (user && user.access_token) {
          // Token is valid, add it to request headers
          config.headers['Authorization'] = `Bearer ${user.access_token}`;
        } else if (token) {
          // Fallback: use the token from localStorage if user object doesn't have it
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.log('Error checking/refreshing token:', error);
        // If we can't refresh, redirect to login
        auth_dispatch({ type: USER_LOGOUT });
        auth.user_manager.signinRedirect();
        return Promise.reject(error);
      }
    } else if (token) {
      // No user_manager available but token exists, use it
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor to handle 401 responses and attempt token refresh
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        auth.user_manager &&
        auth.user
      ) {
        originalRequest._retry = true;

        try {
          console.log('401 response, attempting token refresh...');
          const refreshedUser = await auth.user_manager.signinSilent();

          if (refreshedUser && refreshedUser.access_token) {
            // Update the token in localStorage and auth context
            localStorage.setItem('accqsure_jwt', refreshedUser.access_token);
            auth_dispatch({ type: SET_AUTH_USER, payload: refreshedUser });

            // Retry the original request with the new token
            originalRequest.headers['Authorization'] =
              `Bearer ${refreshedUser.access_token}`;
            console.log('Token refreshed and retrying request');
            return api(originalRequest);
          } else {
            // Refresh failed, redirect to login
            console.log('Token refresh failed, redirecting to login');
            auth_dispatch({ type: USER_LOGOUT });
            auth.user_manager.signinRedirect();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          console.log('Token refresh failed:', refreshError);
          // If we can't refresh, redirect to login
          auth_dispatch({ type: USER_LOGOUT });
          auth.user_manager.signinRedirect();
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );

  return api;
}

export function ApiProvider({ children }: ApiProviderProps) {
  const auth = useAuth();
  const auth_dispatch = useAuthDispatch();
  const [api, setApi] = useState<AxiosInstance | null>(null);

  useEffect(() => {
    if (auth.user_manager) {
      setApi(() => {
        // must return this inside a function otherwise it tries to execute it as a function
        return buildApi(auth, auth_dispatch);
      });
    }
  }, [auth, auth_dispatch]);

  // Proactive token refresh - check token expiration every 5 minutes
  useEffect(() => {
    if (!api || !auth.user_manager || !auth.user) return;

    const userManager = auth.user_manager; // Capture to avoid null check issues in async

    const checkAndRefreshToken = async () => {
      try {
        const user = await userManager.getUser();
        if (user && user.expires_at) {
          const timeUntilExpiry = user.expires_at - Date.now() / 1000;
          // If token expires in less than 10 minutes, refresh it
          if (timeUntilExpiry < 600) {
            console.log(
              `Token expiring in ${Math.round(timeUntilExpiry)} seconds, proactively refreshing...`,
            );
            try {
              const refreshedUser = await userManager.signinSilent();
              if (refreshedUser && refreshedUser.access_token) {
                localStorage.setItem(
                  'accqsure_jwt',
                  refreshedUser.access_token,
                );
                auth_dispatch({ type: SET_AUTH_USER, payload: refreshedUser });
                console.log('Token refreshed proactively');
              } else {
                console.log('Proactive token refresh returned no user');
              }
            } catch (error) {
              console.log('Proactive token refresh failed:', error);
              // Don't redirect to login here as the user might still be active
              // The request interceptor will handle this when making actual API calls
            }
          }
        }
      } catch (error) {
        console.log('Error checking token expiration:', error);
        // Don't redirect to login here as the user might still be active
        // The request interceptor will handle this when making actual API calls
      }
    };

    // Check immediately
    checkAndRefreshToken();

    // Then check every 5 minutes
    const interval = setInterval(checkAndRefreshToken, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [api, auth.user_manager, auth.user, auth_dispatch]);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

export function useApi(): AxiosInstance | null {
  return useContext(ApiContext);
}
